// Product brief for Nationwide CareMatters II. Hand-written prose describing
// the mechanics that are NOT visible from the numbers alone, so a reviewer
// reading the transcript can spot product-semantic mistakes. Edit freely.

export const cmTwoBrief = `**Nationwide CareMatters II** — indemnity hybrid life/LTC.

Pool model:
- The **base benefit pool** is FLAT. It does not inflate. Its lifetime ceiling
  is (at-issue monthly benefit × benefit-period months) and stays constant
  across every year snapshot — so a per-year-growing base pool ceiling is a bug.
- There is **no EOB (extension-of-benefits) pool**. The \`eob\` column should be
  $0 on every row. Any EOB drawdown indicates the wrong product mechanics.
- Growth lives entirely in a **rider monthly stream** layered on top of the base
  monthly benefit. The rider escalates on the policy's inflation rider rate
  (\`inflation_rate\` in the header — can be simple or compound, with a cap year)
  and steps on **policy anniversaries regardless of claim status** (P2-1; the
  carrier's clock — an accepted divergence from the legacy engine's paid-month
  clock, which paused the rider during the EP and non-paying stretches).
  This is why the effective daily benefit grows across year snapshots while the
  base pool ceiling does not.

Payout model — **indemnity**:
- A paying month pays the full monthly benefit (base + rider) regardless of
  actual care cost; the policy does not reimburse to cost. When the benefit
  exceeds the month's care cost, the excess accrues as **indemnity surplus**
  (a non-LTC return), NOT as extra LTC coverage.
- Because it is indemnity, \`payout\` can exceed \`care_cost\` on a row, and
  \`ineligible\` (out-of-pocket) is floored at $0 on paying months.
- A row pays the regular monthly benefit only while the pool can fund a FULL
  month. On the first eligible row where it can't, the sub-monthly remainder
  is paid out as indemnity cash and banked into the surplus reserve (P2-4a) —
  the pool closes at $0, nothing is stranded.
- The EP retroactive lump covers its own row's remaining cost first; any
  excess is banked into the surplus reserve (P2-4b). \`ineligible\` never goes
  negative — a lump bigger than the month's cost shows up as surplus, not as
  a negative out-of-pocket credit.

Elimination period (EP / waiting period):
- The EP is served once per claimant. On satisfaction, CM II pays a **single
  retroactive lump** for the waiting-period days at the full monthly benefit
  then in force (base + inflation rider; the carrier example pays 4 × the
  age-85 monthly benefit when month 4 is paid). The \`ep-lump\` milestone fires
  ONCE — an asymmetric home/facility EP must not double-pay.

Eligibility gates (out-of-pocket reasons, in priority order):
- \`pre-policy\` — care before the policy premium start year; policy pays $0.
- \`lt-2-adl\` — early-care months below the 2-ADL benefit trigger.
- \`in-wp\` — 2-ADL gate cleared, still inside the elimination period.

Death benefit:
- The face amount funds the base pool; LTC drawdown reduces the death benefit,
  floored at the minimum guaranteed death benefit. Remaining DB at the horizon
  is a non-LTC return.

Things to eyeball:
- Effective daily benefit grows year-over-year, base pool ceiling stays flat.
- Rider rate in the header matches the snapshot daily-benefit growth.
- EP lump fires exactly once, around the month the waiting period drains.
- EP lump uses the full effective benefit, not base-only dollars.
`;
